import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  defaultNow,
  eulaStandardContractUrl,
  isLoading as Loading,
  LoadingTypes,
  useAccountId,
} from '@labrav/common'
import {
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
  NoteColors,
  NoteV2,
  RadioButtonGroupMapping,
  RadioButtonType,
  TextInputV2,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { Form, Formik, FormikProps } from 'formik'
import { noop } from 'lodash'
import { useState, type Dispatch, type SetStateAction } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { ComponentLoadingState } from '@labrav/common'

import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { activateFreeTrialV2 } from '../../modules/freeTrials/action'
import { Dimension, EulaType } from '../../types/privateOffer'
import { freeTrialValidation } from '../../common/utils/enableFreeTrialValidation'
import { StandardDimensionsV2 } from '../../common/components/StandardDimensionsV2/StandardDimensionsV2'
import { EulaSectionContent } from '../../common/components/EulaSection/EulaSection'
import { useStyles } from './EnableFreeTrial.styles'

type InitialValuesEnableFreeTrialType = {
  dimensions: Dimension[]
  eulaType: EulaType
  eulaFile?: string[]
  numberOfDays: number
}

type LoadingRootState = {
  loading: ComponentLoadingState
}

export type EnableFreeTrialProps = {
  selectedProductId: string
  openFreeTrialModal: boolean
  setOpenFreeTrialModal: Dispatch<SetStateAction<boolean>>
  successFreeTrial: () => void
}

export const EnableFreeTrial = ({
  openFreeTrialModal,
  setOpenFreeTrialModal,
  selectedProductId,
  successFreeTrial,
}: EnableFreeTrialProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout' })
  const isLoadingState = useSelector((state: LoadingRootState) =>
    Loading(state.loading)(LoadingTypes.ACTIVATE_FREE_TRIAL)
  )
  const { products: productRows } = useFlyOutProductsBasedOnSelectedCloud()
  const productDetail = productRows?.find(
    productItem => productItem?.productId === selectedProductId
  )
  const [eulaErr, setEulaErr] = useState<string>('')
  const updateErr = (error: string) => {
    setEulaErr(error)
  }

  const handleCloseFreeTrialModal = () => {
    setOpenFreeTrialModal(false)
  }

  const handleCallBackFreeTrialSuccess = () => {
    handleCloseFreeTrialModal()
    successFreeTrial()
  }
  const handleSubmitEnable = async (
    values: InitialValuesEnableFreeTrialType
  ) => {
    if (accountId) {
      await dispatch(
        activateFreeTrialV2(
          accountId,
          {
            productId: selectedProductId,
            eulaFiles: values.eulaFile,
            eulaType: values.eulaType,
            cloudMarketplace: productDetail?.cloudMarketplace,
            cloudFreeTrialOfferName: `${productDetail?.productName}: Free trial`,
            freeTrialExpiresInDays: Number(values.numberOfDays),
            freeTrialDimensionData: values?.dimensions
              .map(dimension => {
                const { dimensionId, quantity, name } = dimension
                const quantityValue = Number(quantity)
                return {
                  productDimensionId: dimensionId,
                  quantity: quantityValue,
                  name,
                }
              })
              .filter(dimension => dimension.quantity !== 0),
          },
          handleCallBackFreeTrialSuccess
        ) as never
      )
    }
  }

  const initialValuesEnableFreeTrial: InitialValuesEnableFreeTrialType = {
    dimensions: [
      {
        label: '',
        type: 'standard',
        currency: 'USD',
      },
    ],
    numberOfDays: 0,
    eulaType: EulaType.PUBLIC,
    eulaFile: [],
  }

  const eulaButtonOptionsV2: RadioButtonGroupMapping[] = [
    {
      key: EulaType.PUBLIC,
      label: 'Public offer legal terms',
      type: RadioButtonType.STANDARD,
      url: productDetail?.marketplaceUrl || '',
    },
    {
      key: EulaType.STANDARD,
      label: 'Standard contract for AWS marketplace (SCMP)',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'SCMP is a standardized contract template intended to help streamline procurement and speed up transactions.',
      url: eulaStandardContractUrl,
    },
    {
      key: EulaType.CUSTOM,
      label: 'Custom EULA',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Provide a URL to a custom end-user license agreement (EULA) for your product.',
    },
  ]

  return (
    <ModalConfirmV2
      title={'Enable free trial'}
      open={openFreeTrialModal}
      content={
        <div className={classes.root} data-testid={'enable-free-trial'}>
          <div className={classes.description}>
            Free trials are a way to provide buyers the option to evaluate your
            AWS Marketplace software as a service (SaaS) product before making a
            purchase. Offering free trials can help you increase the number of
            potential buyers who consider and purchase your product while
            evaluating their SaaS product options. After a buyer subscribes to
            your product, your product performs entitlement checks the same way
            it does for paid customers.
          </div>
          <div className={classes.note}>
            <NoteV2
              backgroundColor={NoteColors.YELLOW}
              description={
                "Free Trial will go live only once the listing is in the 'Public' status."
              }
            />
          </div>
          <Formik
            initialValues={initialValuesEnableFreeTrial}
            validationSchema={freeTrialValidation}
            validateOnMount={true}
            onSubmit={async values => {
              await handleSubmitEnable(values)
            }}
            enableReinitialize
          >
            {formikProps => {
              const handleSubmit = async () => {
                await formikProps?.submitForm()
              }

              const { values, touched, errors, handleBlur } = formikProps

              return (
                <Form>
                  <Typography className={classes.heading}>
                    Product dimensions
                  </Typography>
                  <Typography className={classes.subValue}>
                    Choose the dimensions & quantities you want to offer under
                    free trial.
                  </Typography>
                  <StandardDimensionsV2
                    isFlexible
                    hideNote={true}
                    formik={
                      formikProps as unknown as FormikProps<
                        Record<string, unknown>
                      >
                    }
                    productId={selectedProductId}
                    addButtonName="Add more dimensions"
                  />
                  <div className={classes.numberOfDaysWrapper}>
                    <Typography className={classes.heading}>
                      Number of free trial days
                    </Typography>
                    <Typography className={classes.subValue}>
                      This is the description of your plan that helps your
                      buyers understand its offerings.
                    </Typography>
                    <div className={classes.numberOfDays}>
                      <TextInputV2
                        keyName="numberOfDays"
                        title={'Enter days'}
                        supportingText="Should be between 7-90 days."
                        type="number"
                        placeHolder=""
                        size={'medium'}
                        value={values?.numberOfDays || ''}
                        updatedValue={values?.numberOfDays || ''}
                        onBlur={handleBlur}
                        onChange={e =>
                          formikProps.setFieldValue(
                            'numberOfDays',
                            e?.target?.value
                          )
                        }
                        isError={
                          !!(errors?.numberOfDays && touched?.numberOfDays)
                        }
                        errorText={
                          (!!touched?.numberOfDays && errors?.numberOfDays) ||
                          ''
                        }
                        isReadOnly={false}
                        isRequired={false}
                        showSpinner={false}
                      />
                    </div>
                  </div>
                  <div className={classes.eulaWrapper}>
                    <EulaSectionContent
                      defaultNow={defaultNow}
                      eulaDescription={
                        <div className={classes.eulaDesc}>
                          At the point of purchase, buyers must agree to the
                          terms and conditions outlined in your product's
                          end-user license agreement (EULA).
                        </div>
                      }
                      formik={
                        formikProps as unknown as FormikProps<
                          Record<string, unknown>
                        >
                      }
                      isMultiFileUpload={true}
                      fileUploadDescription={
                        'Upload up to 5 files one by one, use plain text .txt or PDF formats only. The maximum file size is 10 MB.'
                      }
                      fileUploadButtonText={'Upload files'}
                      fileUploadContainerTitle={'Drag and drop files here'}
                      quantity={5}
                      eulaOptionsHeading={'Legal term documents'}
                      isOptional={values?.eulaType !== EulaType.CUSTOM}
                      eulaOptionSubHeading={
                        values?.eulaType === EulaType.STANDARD
                          ? 'Upload up to 5 files one by one to amend the Standard contract for AWS marketplace, use plain text .txt or PDF formats only. The maximum file size is 10 MB. Once uploaded, all files will be combined into a single PDF. The filed will be presented to buyers in the order they are uploaded.'
                          : 'Upload up to 5 files one by one using plain text (.txt) or PDF formats only. The maximum file size is 10 MB. Once uploaded, all files will be combined into a single PDF. The files will be presented to buyers in the order they are uploaded'
                      }
                      showEulaFileUpload={true}
                      eulaOptions={eulaButtonOptionsV2}
                      fileSize={10}
                      updateErr={updateErr}
                      uploadError={
                        eulaErr ||
                        (formikProps?.touched.eulaFile
                          ? (formikProps?.errors?.eulaFile as string)
                          : '')
                      }
                    />
                  </div>
                  <div className={classes.rowRightItem}>
                    <ButtonV2
                      data-testid="cancel-free-trial"
                      smallVariant
                      styleType="outlined"
                      onClick={handleCloseFreeTrialModal}
                    >
                      Cancel
                    </ButtonV2>

                    <ButtonV2
                      data-testid="save-free-trial"
                      smallVariant
                      styleType="primary"
                      onClick={handleSubmit}
                      loading={isLoadingState}
                    >
                      Enable free trial
                    </ButtonV2>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      }
      onDecline={handleCloseFreeTrialModal}
      variant={ModalSizeVariants.xLarge}
      icon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '20px', height: '16px' }}
        />
      }
      hideCancelIcon={false}
      hideCancelButton={true}
      hideAcceptButton={true}
      onAccept={noop}
    />
  )
}
